import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Printer, Download, X } from "lucide-react";

/**
 * Floating "Download PDF" button shown on every page.
 *
 * Behaviour:
 *  - Always offers "Print / Save as PDF" (uses the global print.css, works
 *    everywhere — login screens, dashboards, content pages alike).
 *  - On supported content routes (clinical reasoning topics, pediatrics
 *    conditions, explain catalog), ALSO offers a high-fidelity
 *    server-rendered branded PDF download.
 *  - Hides itself automatically on routes where a PDF makes no sense
 *    (auth, callbacks, raw embeds).
 *
 * The button itself carries the .no-print class so it never appears in
 * the printed/exported PDF.
 */

const HIDE_ON_ROUTES = [
  /^\/login\b/,
  /^\/auth\/callback\b/,
  /^\/magic-login\b/,
  /^\/verify-email\b/,
  /^\/reset-password\b/,
  /^\/forgot-password\b/,
  /^\/accept-invite\b/,
  // CR 2.0 workspace ships its own command bar with PDF links.
  /^\/clinical-reasoning\/condition\//,
];

// Map URL pattern → server PDF endpoint resolver.
// Returns { module, slug, title } if the current route supports server-side
// branded PDF, otherwise null.
function resolveServerPdf(pathname) {
  // Clinical reasoning topic:  /clinical-reasoning/:slug
  // ("compare" and "condition" are app sub-routes, not topics)
  let m = pathname.match(/^\/clinical-reasoning\/([\w-]+)(?:\/|$)/);
  if (m && m[1] !== "compare" && m[1] !== "condition") return { module: "clinical-reasoning", slug: m[1] };

  // Pediatrics condition:  /pediatrics/condition/:slug
  m = pathname.match(/^\/pediatrics\/condition\/([\w-]+)(?:\/|$)/);
  if (m) return { module: "pediatrics", slug: m[1] };

  // Explain disease page:  /explain/:slug
  m = pathname.match(/^\/explain\/([\w-]+)(?:\/|$)/);
  if (m) return { module: "explain", slug: m[1] };

  return null;
}

export default function GlobalPdfFab() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState(null);
  const ref = useRef(null);

  // Close popover on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Hide on auth / callback routes.
  const shouldHide = HIDE_ON_ROUTES.some((re) => re.test(location.pathname));

  const serverPdf = resolveServerPdf(location.pathname);

  const handlePrint = useCallback(() => {
    setOpen(false);
    // Slight defer so the popover unmounts before the print dialog opens.
    setTimeout(() => window.print(), 50);
  }, []);

  const handleServerDownload = useCallback(async () => {
    if (!serverPdf) return;
    setDownloading(true);
    setErr(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = `${base}/api/pdf/${serverPdf.module}/${encodeURIComponent(
        serverPdf.slug
      )}.pdf`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${serverPdf.module}-${serverPdf.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      setOpen(false);
    } catch (e) {
      setErr(e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  }, [serverPdf]);

  if (shouldHide) return null;

  return (
    <div
      ref={ref}
      className="no-print"
      style={{
        position: "fixed",
        right: "20px",
        bottom: "20px",
        zIndex: 90,
      }}
      data-testid="global-pdf-fab"
    >
      {open && (
        <div
          className="rounded-2xl shadow-2xl"
          style={{
            position: "absolute",
            bottom: "60px",
            right: 0,
            width: "260px",
            background: "rgba(10, 20, 26, 0.96)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(107, 143, 113, 0.22)",
            color: "#e2e8f0",
            padding: "12px 12px 10px",
            fontFamily: "Georgia, 'Source Serif Pro', serif",
          }}
          data-testid="global-pdf-menu"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <span
              style={{
                fontSize: "10.5px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#6B8F71",
              }}
            >
              Save this page
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="pdf-menu-close"
              style={{
                background: "transparent",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer",
                padding: "2px",
              }}
            >
              <X size={14} />
            </button>
          </div>

          {serverPdf && (
            <button
              type="button"
              onClick={handleServerDownload}
              disabled={downloading}
              data-testid="pdf-download-branded"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                background: "rgba(107, 143, 113, 0.12)",
                color: "#e6f7ff",
                border: "1px solid rgba(107, 143, 113, 0.38)",
                borderRadius: "10px",
                padding: "10px 12px",
                marginBottom: "8px",
                cursor: downloading ? "wait" : "pointer",
                fontSize: "13px",
                textAlign: "left",
                fontFamily: "inherit",
              }}
            >
              <Download size={15} />
              <span style={{ flex: 1 }}>
                {downloading ? "Building PDF…" : "Branded PDF (recommended)"}
              </span>
            </button>
          )}

          <button
            type="button"
            onClick={handlePrint}
            data-testid="pdf-download-print"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              background: "rgba(255, 255, 255, 0.06)",
              color: "#e2e8f0",
              border: "1px solid rgba(255, 255, 255, 0.18)",
              borderRadius: "10px",
              padding: "10px 12px",
              cursor: "pointer",
              fontSize: "13px",
              textAlign: "left",
              fontFamily: "inherit",
            }}
          >
            <Printer size={15} />
            <span style={{ flex: 1 }}>Print or Save as PDF</span>
          </button>

          {err && (
            <div
              data-testid="pdf-fab-error"
              style={{
                marginTop: "8px",
                fontSize: "11px",
                color: "#fda4af",
                lineHeight: 1.4,
              }}
            >
              {err}
            </div>
          )}

          <div
            style={{
              marginTop: "10px",
              fontSize: "10.5px",
              color: "#94a3b8",
              lineHeight: 1.45,
            }}
          >
            Pages save with a Maldek by Hill header, page numbers, and the
            &quot;educational use only&quot; footer.
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Save this page as PDF"
        data-testid="global-pdf-fab-button"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          background: "rgba(10, 20, 26, 0.92)",
          color: "#6B8F71",
          border: "1px solid rgba(107, 143, 113, 0.42)",
          borderRadius: "999px",
          padding: "10px 16px 10px 12px",
          cursor: "pointer",
          fontFamily: "Georgia, 'Source Serif Pro', serif",
          fontSize: "12.5px",
          letterSpacing: "0.04em",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        <Download size={15} />
        <span>PDF</span>
      </button>
    </div>
  );
}
